import React from 'react';
import { ChevronRight, Cog } from 'lucide-react';

import { BottomSheet, Spinner } from './primitives';
import { Colors } from './theme';

/**
 * Pick a machine.
 *
 * Each row is the machine and what it is already carrying — free machines sort
 * to the top on the server, so the obvious choice is the first thing under the
 * finger and reading the list is optional.
 *
 * The sheet only opens when there is a real choice: with one machine linked to
 * the stage, `useAssignMachine` assigns it without showing anything.
 */
export default function MachinePicker({
  open, onClose, stageName, subtitle, machines, loadingMachines, assigning, onAssign,
}) {
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      testID="machine-picker"
      title="Put it on which machine?"
      subtitle={`${stageName}${subtitle ? ` · ${subtitle}` : ''}`}
    >
      {loadingMachines ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (
        <div className="max-h-[21rem] overflow-y-auto">
          {machines.map((machine) => {
            const load = machine.open_job_cards ?? 0;
            return (
              <button
                key={machine.id}
                type="button"
                data-testid={`assign-machine-${machine.id}`}
                onClick={() => onAssign(machine)}
                disabled={assigning}
                className="flex w-full items-center gap-3 border-b py-3.5 text-left last:border-b-0 disabled:opacity-60"
                style={{ borderColor: Colors.border.subtle }}
              >
                <span
                  className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: load === 0 ? Colors.status.success : Colors.text.tertiary }}
                >
                  <Cog className="h-5 w-5" style={{ color: Colors.text.inverse }} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>
                    {machine.machine_name || 'Unnamed machine'}
                  </span>
                  <span
                    className="block text-[11px] font-medium"
                    style={{ color: load === 0 ? Colors.status.success : Colors.text.secondary }}
                  >
                    {load === 0 ? 'Free' : `${load} job${load === 1 ? '' : 's'} on it`}
                  </span>
                </span>
                {assigning
                  ? <Spinner size="sm" />
                  : <ChevronRight className="h-5 w-5 shrink-0" style={{ color: Colors.text.tertiary }} />}
              </button>
            );
          })}
        </div>
      )}
    </BottomSheet>
  );
}
